import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../api_config";
import UserContext from "../../Components/Authcontext";
import Load from "../Loading/Load";
import "./modern-forms.css";

function ModernImageUpload() {
  const { token } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const navigate = useNavigate();

  // Define the form steps
  const totalSteps = 8;
  const currentStep = 4;

  // Calculate progress percentage
  const progressPercentage = (currentStep / totalSteps) * 100;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
      setUploadStatus("");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
      setUploadStatus("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setUploadStatus("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    setLoading(true);

    axios
      .post(`${API_BASE_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      })
      .then((response) => {
        setUploadStatus(response.data.message);
        setLoading(false);
        navigate("/modern-family-info", { replace: true });
      })
      .catch((error) => {
        setUploadStatus("Failed to upload image");
        setLoading(false);
      });
  };

  return loading ? (
    <Load />
  ) : (
    <div className="profile-form-container">
      <div className="profile-form-card">
        {/* Form Header with Progress Bar */}
        <div className="profile-form-header">
          <div className="progress-container">
            <div className="progress-steps">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className="progress-step">
                  <div
                    className={`step-number ${i + 1 === currentStep ? "active" : ""} ${
                      i + 1 < currentStep ? "completed" : ""
                    }`}
                  >
                    {i + 1 < currentStep ? "✓" : i + 1}
                  </div>
                  <div
                    className={`step-label ${i + 1 === currentStep ? "active" : ""} ${
                      i + 1 < currentStep ? "completed" : ""
                    }`}
                  >
                    {i + 1 === 1 && "Basic Info"}
                    {i + 1 === 2 && "Contact"}
                    {i + 1 === 3 && "Personal"}
                    {i + 1 === 4 && "Photos"}
                    {i + 1 === 5 && "Family"}
                    {i + 1 === 6 && "Lifestyle"}
                    {i + 1 === 7 && "Religious"}
                    {i + 1 === 8 && "Preferences"}
                  </div>
                </div>
              ))}
              <div className="progress-line">
                <div
                  className="progress-line-fill"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
          <h2 className="profile-form-title">Profile Photo</h2>
          <p className="profile-form-subtitle">Upload your best photo</p>
          <p className="required-fields-note">Photo is required</p>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit}>
          <div className="profile-form-body">
            <div className="image-upload-container">
              <div 
                className={`image-upload-area ${dragActive ? "drag-active" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {previewUrl ? (
                  <div className="image-preview-container">
                    <img src={previewUrl} alt="Preview" className="image-preview" />
                    <button 
                      type="button" 
                      className="change-image-btn"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      Change Photo
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="upload-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                    <p className="upload-text">Drag & Drop your photo here</p>
                    <p className="upload-text-small">or</p>
                    <label className="file-input-label">
                      Browse Files
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="file-input"
                      />
                    </label>
                    <p className="upload-hint">Supported formats: JPG, PNG, GIF (Max 5MB)</p>
                  </>
                )}
              </div>
            </div>

            {uploadStatus && (
              <div className={`upload-status ${uploadStatus.includes("Failed") ? "error" : "success"}`}>
                {uploadStatus}
              </div>
            )}

            <div className="photo-guidelines">
              <h3 className="guidelines-title">Photo Guidelines</h3>
              <ul className="guidelines-list">
                <li>Upload a recent, clear photo of yourself</li>
                <li>Your face should be clearly visible</li>
                <li>Avoid group photos or photos with filters</li>
                <li>Professional or well-lit casual photos work best</li>
                <li>The photo should represent you accurately</li>
              </ul>
            </div>
          </div>

          {/* Form Footer with Buttons */}
          <div className="profile-form-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => navigate("/modern-personal-info")}
            >
              Back
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={!selectedFile}
            >
              Save & Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModernImageUpload;
